import React, { useCallback, useEffect, useState } from 'react';

import { usePathname, useRouter } from 'expo-router';
import { useExperimentGatedAppTakeover } from 'features/app-takeover/hooks/use-app-takeover';
import { useMaybePlayAudioOnceOnMount } from 'features/app-takeover/utils/play-audio-once';
import LegacyCartContent, { ICartContent } from 'features/cart/components/legacy-cart-content';
import { hasStoredCartEntries } from 'features/cart/utils/stored-cart-entries';
import { usePaymentContext } from 'features/payment';
import { useEnableServiceRewardsBrowse } from 'features/reward/state/service-rewards-context';
import { useStoreContext } from 'features/store';
import { Spinner } from 'native-base';

import { CardModal } from 'components/card-modal';
import { RemoveUnavailableItemsModal } from 'components/remove-unavailable-items-modal';
import { VisuallyHidden } from 'components/ucl/visually-hidden';
import useAuthRedirects from 'hooks/auth/use-auth-redirects';
import { useCart } from 'hooks/use-cart';
import useDebounceEffect from 'hooks/use-debounce-effect';
import useEffectOnce from 'hooks/use-effect-once';
import useFeatureMenuAddOn from 'hooks/use-feature-menu-add-on';
import useFoodware from 'hooks/use-foodware';
import useIsSignUpAfterCartEnabled from 'hooks/use-is-sign-up-after-cart';
import { usePrevious } from 'hooks/use-previous';
import { logCheckoutEvent } from 'state/amplitude';
import { useCartContext } from 'state/cart';
import { useIsLegacyCartReady } from 'state/cart/hooks/use-is-legacy-cart-ready';
import { selectors, useAppSelector } from 'state/global-state';
import { useLocale } from 'state/intl';
import { t } from 'state/intl/translations';
import { useMainMenuContext } from 'state/menu/main-menu';
import { useOrderContext } from 'state/order';
import { useServiceModeLive } from 'state/service-mode';
import { useUIContext } from 'state/ui';
import { cartHasLegacyOrderableEntries, isLegacyCondimentsOnlyCart } from 'utils/legacy-condiments';
import { routes } from 'utils/routing';

import { cartHasItemsOrCombos, shouldRedirectToMenu } from '../../utils/cart-utils';

const CartContent = (props: ICartContent) => {
  useMaybePlayAudioOnceOnMount(useExperimentGatedAppTakeover()?.cart?.audio, {
    priority: 1,
    globalPlayOnce: true,
  });

  return <LegacyCartContent {...props} />;
};

type CartProps = {
  shouldEvaluateCartRedirect?: boolean;
};

export const Cart = ({ shouldEvaluateCartRedirect = true }: CartProps) => {
  useEnableServiceRewardsBrowse();
  const { cartEntries, calculateCartTotal } = useCart();
  const minimalCartEntries = useAppSelector(selectors.ordering.selectMinimalCartEntries);
  const pricedOrderId = useAppSelector(selectors.ordering.selectPricedOrderId);
  const isLegacyCartReady = useIsLegacyCartReady();
  const {
    serverOrder,
    // TODO: BKPE-1956 - unavailableCartEntries
    unavailableCartEntries,
    orderStatus,
    cardSuccessfullyAdded,
    tryPricing,
    shouldSkipPriceOrder,
    setShouldSkipPriceOrder,
    isOrderPriceLoading,
  } = useCartContext();
  const serviceMode = useServiceModeLive();

  const { checkoutPaymentMethod } = usePaymentContext();
  const { featureFoodware } = useFoodware();
  const { sections: addOnSections } = useFeatureMenuAddOn({ ignoreEnableAddExtras: true });
  const isSignUpAfterCartEnabled = useIsSignUpAfterCartEnabled();
  const { isStoreOpenAndAvailable, store } = useStoreContext();
  const { getStoreMenu } = useMainMenuContext();
  const { region } = useLocale();
  const pathname = usePathname();
  const isCartRoute = pathname === routes.cart;
  const isCartPricingRoute = pathname.startsWith(routes.cart);
  const appliedLoyaltyRewardsArray = useAppSelector(
    selectors.loyalty.selectAppliedLoyaltyRewardsArray
  );
  const router = useRouter();
  const { isStoreOrderUnavailableDialogOpen } = useUIContext();
  const storeId = store.number || '';

  const { checkoutRepriceRequestId, isStoreChangeHydrationInProgress } = useOrderContext();

  // DO NOT USE service mode on order context. Use service mode on server order
  const orderServiceMode = serverOrder?.cart?.serviceMode;
  const isLoadingOffers = useAppSelector(selectors.loyalty.selectOffersLoading);
  const isLoading = isOrderPriceLoading || isLoadingOffers;
  const unavailableCartItems = unavailableCartEntries.length > 0;
  const [showUnavailableItemsModal, setShowUnavailableItemsModal] = useState(false);
  const serverOrderStoreId = serverOrder?.cart?.storeId?.toString?.() ?? '';
  const isServerOrderSettledForCurrentStoreAndServiceMode =
    Boolean(serverOrderStoreId && storeId) &&
    serverOrderStoreId === storeId &&
    Boolean(orderServiceMode && serviceMode) &&
    orderServiceMode === serviceMode;

  // check if cart entries don't have item or combos
  const hasItemsOrCombos = cartHasItemsOrCombos(cartEntries);
  const hasRecoverableMinimalCartEntries = minimalCartEntries.length > 0;
  const hasRecoverablePricedOrder = Boolean(pricedOrderId);
  // Only trust storage as a recovery signal while the in-memory cart is still empty (pre-hydration
  // window). Once Redux has hydrated, `shouldRedirectToMenu` must remain the authority so a
  // non-qualifying cart (e.g. donation-only) or a missing service mode still redirects to menu.
  const isInMemoryCartEmpty = cartEntries.length === 0 && minimalCartEntries.length === 0;
  const hasOrderableEntries = cartHasLegacyOrderableEntries(cartEntries);
  const foodwareItemIds = (featureFoodware?.items ?? []).flatMap(item =>
    item?._id ? [item._id] : []
  );
  const isLegacyCondimentsOnly = isLegacyCondimentsOnlyCart(
    cartEntries,
    addOnSections,
    foodwareItemIds
  );
  // to allow below dismissAndReplace to run with expo router which can't navigate on
  // first render if cart deep link was used
  const [isMounted, setIsMounted] = useState(false);

  useAuthRedirects({ skip: isSignUpAfterCartEnabled });

  const prevCartEntriesLength = usePrevious(cartEntries.length);

  useEffectOnce(() => {
    setIsMounted(true);
  });

  useEffect(() => {
    if (isStoreOpenAndAvailable && serviceMode && cartEntries.length > 0) {
      logCheckoutEvent({
        serviceMode,
        cartEntries,
        shouldLogIndividualProducts: !prevCartEntriesLength,
      });
    }
  }, [cartEntries, isStoreOpenAndAvailable, serviceMode, prevCartEntriesLength]);

  // A discount offer's CMS/incentive data can resolve after this screen's
  // initial price request already fired (e.g. on a fresh reload) — `cartEntries`
  // here is the raw legacy cart (unaffected by that resolution), so without
  // this the server-priced total shown/sent for checkout would permanently
  // exclude the discount. `calculateCartTotal()` already recomputes correctly
  // once the offer data lands (see `useCart`), so keying the reprice trigger
  // on its value re-fires exactly when the real price changes. See BKPE-28076.
  const cartTotalForPricing = calculateCartTotal();

  useDebounceEffect(
    () => {
      if (
        !isStoreOpenAndAvailable ||
        !serviceMode ||
        !hasOrderableEntries ||
        isStoreChangeHydrationInProgress
      ) {
        return;
      }

      shouldSkipPriceOrder && setShouldSkipPriceOrder(false);

      tryPricing();
    },
    250,
    [
      appliedLoyaltyRewardsArray,
      cartEntries,
      cartTotalForPricing,
      hasOrderableEntries,
      isStoreOpenAndAvailable,
      serviceMode,
      tryPricing,
    ]
  );

  useDebounceEffect(
    () => {
      if (
        !checkoutRepriceRequestId ||
        !isCartPricingRoute ||
        !isStoreOpenAndAvailable ||
        !serviceMode ||
        !hasOrderableEntries ||
        isStoreChangeHydrationInProgress
      ) {
        return;
      }

      shouldSkipPriceOrder && setShouldSkipPriceOrder(false);

      tryPricing();
    },
    250,
    [
      checkoutRepriceRequestId,
      isCartPricingRoute,
      hasOrderableEntries,
      isStoreChangeHydrationInProgress,
      isStoreOpenAndAvailable,
      serviceMode,
      tryPricing,
    ]
  );

  useEffect(() => {
    if (storeId) {
      getStoreMenu({ region, storeId });
    }
  }, [getStoreMenu, region, storeId]);

  useEffect(() => {
    setShowUnavailableItemsModal(
      !isLoading &&
        !isStoreChangeHydrationInProgress &&
        isServerOrderSettledForCurrentStoreAndServiceMode &&
        unavailableCartItems &&
        !isStoreOrderUnavailableDialogOpen
    );
  }, [
    isLoading,
    isStoreChangeHydrationInProgress,
    isServerOrderSettledForCurrentStoreAndServiceMode,
    unavailableCartItems,
    isStoreOrderUnavailableDialogOpen,
  ]);

  const handleUnavailableModalDismiss = useCallback(() => {
    setShowUnavailableItemsModal(false);
    tryPricing();
  }, [tryPricing]);

  useEffect(() => {
    // Since multiple routes are under the same (cart) route group,
    // the cart component doesn't unmount when navigating to other routes like (/checkout), causing
    // isMounted to remain true. This ensures isMounted is only true when actually
    // on the cart route, preventing premature navigation logic execution.
    setIsMounted(isCartRoute);
  }, [isCartRoute]);

  useEffect(() => {
    if (
      !(
        shouldEvaluateCartRedirect &&
        !hasRecoverableMinimalCartEntries &&
        !hasRecoverablePricedOrder &&
        // On a hard reload the cart route can evaluate this before Redux hydrates from storage, so
        // an empty in-memory cart is not proof there is nothing to recover. Consult storage only
        // during that window; once hydrated, `shouldRedirectToMenu` is the authority.
        !(isInMemoryCartEmpty && hasStoredCartEntries()) &&
        shouldRedirectToMenu({
          isMounted,
          cartEntries,
          orderStatus,
          serviceMode,
          orderServiceMode,
          hasItemsOrCombos,
          redirectWhenNoQualifyingItems: !isLegacyCondimentsOnly,
        })
      )
    ) {
      return;
    }

    // Add delay to ensure iOS native modal view cache is cleared before navigation
    // This prevents cached ActionSheet snapshots from appearing during the transition.
    // Keep the handles so a later render that recovers the cart (minimal entries or a priced
    // order hydrating after mount) cancels a redirect scheduled off a transient empty state.
    let timeoutId: ReturnType<typeof setTimeout>;
    const rafId = requestAnimationFrame(() => {
      timeoutId = setTimeout(() => {
        router.replace(routes.menu);
      });
    });

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timeoutId);
    };
  }, [
    router,
    isMounted,
    cartEntries,
    hasRecoverableMinimalCartEntries,
    hasRecoverablePricedOrder,
    isInMemoryCartEmpty,
    orderStatus,
    serviceMode,
    orderServiceMode,
    hasItemsOrCombos,
    isLegacyCondimentsOnly,
    shouldEvaluateCartRedirect,
  ]);

  if (!isLegacyCartReady) {
    return (
      <CardModal
        key="cart"
        dismissType="back"
        invertHeaderColor
        headerText={t('yourOrder')}
        children={<Spinner marginTop={'50%'} />}
        webAnimate={false}
        isFullScreen
      />
    );
  }

  if (showUnavailableItemsModal) {
    return (
      <RemoveUnavailableItemsModal
        onConfirmCb={handleUnavailableModalDismiss}
        unavailableCartEntries={unavailableCartEntries}
      />
    );
  }

  return (
    <>
      <CartContent checkoutPaymentMethod={checkoutPaymentMethod} />
      {cardSuccessfullyAdded && (
        <VisuallyHidden accessibilityLabel={t('yourNewPaymentMethodHasBeenSuccessfullySaved')} />
      )}
    </>
  );
};
